import { useState } from "react";
import { RefreshCw, Clock, Mail, ChefHat, Cog, Wrench, Facebook, Instagram, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/constants/site";

const SUPPORT_EMAIL = "support@halalgo.ph";

const SOCIALS = [
  { label: "Facebook", icon: Facebook },
  { label: "Instagram", icon: Instagram },
  { label: "YouTube", icon: Youtube },
];

// Friendly "kitchen tuning up" illustration built from brand-tinted shapes so it
// stays crisp, on-brand, and dependency-free.
const TuningIllustration = () => (
  <div className="relative mx-auto flex h-40 w-40 items-center justify-center" aria-hidden="true">
    <div className="absolute inset-0 rounded-full bg-halalgo-green-50" />
    <div className="absolute inset-4 rounded-full border-2 border-dashed border-halalgo-green-200" />
    <ChefHat className="relative h-16 w-16 text-halalgo-green-700" strokeWidth={1.6} />
    <span className="absolute -right-1 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lift-sm ring-1 ring-halalgo-kraft-200">
      <Cog className="h-7 w-7 animate-spin text-halalgo-kraft-700 [animation-duration:6s]" strokeWidth={1.8} />
    </span>
    <span className="absolute -left-1 bottom-3 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-lift-sm ring-1 ring-halalgo-kraft-200">
      <Wrench className="h-5 w-5 text-halalgo-green-600" strokeWidth={1.8} />
    </span>
  </div>
);

export default function Maintenance({ status, onRefresh, refreshing }) {
  const [checking, setChecking] = useState(false);
  const estimatedTime = status?.estimated_time || "We'll be back online shortly.";
  const message =
    status?.message ||
    "Halal GO is currently undergoing scheduled maintenance to improve your experience. We're tuning up our engines to bring you better service.";

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setChecking(true);
    try {
      await onRefresh();
    } finally {
      setChecking(false);
    }
  };

  const busy = checking || refreshing;

  return (
    <div className="paper-bg flex min-h-screen flex-col items-center px-4 py-10 text-halalgo-ink" data-testid="maintenance-page">
      {/* Brand */}
      <div className="mb-8 flex items-center gap-3">
        <img
          src={SITE.logo}
          alt="Halal GO logo"
          width={44}
          height={44}
          className="h-11 w-11 rounded-lg object-cover shadow-sm ring-1 ring-halalgo-kraft-200"
        />
        <div className="leading-tight">
          <p className="text-lg font-bold text-halalgo-green-800">{SITE.name}</p>
          <p className="text-xs font-medium text-halalgo-ink/55">{SITE.microTagline}</p>
        </div>
      </div>

      {/* Card */}
      <div className="w-full max-w-xl rounded-3xl border border-halalgo-kraft-100 bg-white p-8 text-center shadow-lift-sm sm:p-10">
        <TuningIllustration />

        <span
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700"
          data-testid="maintenance-status-pill"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
          </span>
          Under Maintenance
        </span>

        <h1 className="mt-5 text-3xl font-bold tracking-tight text-halalgo-ink sm:text-4xl" data-testid="maintenance-headline">
          We&rsquo;ll Be Right Back!
        </h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-halalgo-ink/70">
          {message}
        </p>

        {/* Estimated time */}
        <div className="mx-auto mt-6 flex max-w-sm items-center gap-3 rounded-xl border border-halalgo-green-200 bg-halalgo-green-50 px-4 py-3 text-left" data-testid="maintenance-eta-card">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-halalgo-green-700 ring-1 ring-halalgo-green-200">
            <Clock className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-halalgo-green-800/70">Estimated Time</p>
            <p className="text-sm font-medium text-halalgo-green-800">{estimatedTime}</p>
          </div>
        </div>

        {/* Refresh */}
        <Button
          onClick={handleRefresh}
          disabled={busy}
          className="mt-7 h-11 bg-halalgo-green-600 px-6 text-white hover:bg-halalgo-green-700"
          data-testid="maintenance-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Checking\u2026" : "Refresh Status"}
        </Button>

        {/* Contact */}
        <div className="mt-8 border-t border-halalgo-kraft-100 pt-6">
          <p className="text-sm text-halalgo-ink/60">Urgent inquiry? Our team is one message away.</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-halalgo-green-700 underline-offset-4 hover:text-halalgo-green-800 hover:underline"
            data-testid="maintenance-support-email"
          >
            <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
          </a>
          <div className="mt-4 flex items-center justify-center gap-2">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href="#"
                aria-label={s.label}
                title={s.label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-halalgo-kraft-200 bg-halalgo-paper2 text-halalgo-ink/60 transition-colors hover:border-halalgo-green-200 hover:bg-halalgo-green-50 hover:text-halalgo-green-700"
                data-testid={`maintenance-social-${s.label.toLowerCase()}`}
              >
                <s.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-8 text-xs text-halalgo-ink/45">{SITE.footerLine}</p>
    </div>
  );
}
